import { ScrollReveal } from '@/components/ui/scroll-reveal';
import { useDocumentHead } from '@/hooks/use-document-head';

const TESTIMONIALS = [
  {
    name: 'Dan the Man!!!',
    city: 'Orlando · Thumbtack',
    rating: 5,
    quote: 'Showed up on time, did exactly what he said, and left the place shining. Already booked again.',
    service: 'Residential Cleaning',
  },
  {
    name: 'They are the best, hands down.',
    city: 'Winter Park · Thumbtack',
    rating: 5,
    quote: 'The attention to detail is on another level. Our office has never looked this good.',
    service: 'Commercial Cleaning',
  },
  {
    name: 'Easy to communicate with, showed up on time.',
    city: 'Altamonte Springs · Thumbtack',
    rating: 5,
    quote: 'Post-construction mess I thought was unsalvageable. They made it showroom-ready. Unreal work.',
    service: 'Post-Construction',
  },
  {
    name: 'Coming Soon',
    city: 'Central Florida',
    rating: 5,
    quote: 'Your review could be here. We\'re building our collection of happy client stories.',
    service: 'Your Experience',
    placeholder: true,
  },
  {
    name: 'Coming Soon',
    city: 'Central Florida',
    rating: 5,
    quote: 'Another spot waiting for a real review from a satisfied SOTSVC client.',
    service: 'Your Experience',
    placeholder: true,
  },
  {
    name: 'Coming Soon',
    city: 'Central Florida',
    rating: 5,
    quote: 'We\'re growing our reviews. Book a clean and share your honest experience.',
    service: 'Your Experience',
    placeholder: true,
  },
];

export default function TestimonialsPage() {
  useDocumentHead(
    'Customer Reviews | Sonz of Thunder Services Orlando',
    'Read customer reviews for Sonz of Thunder Services (SOTSVC), cleaning homes & offices across Orlando & Central Florida.',
  );
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[400px] clip-diag-bottom overflow-hidden">
        <img src="/images/pages/testimonials-hero.png" alt="" className="absolute inset-0 w-full h-full object-cover ken-burns" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(15,15,30,.9) 0%, rgba(15,15,30,.55) 60%, rgba(0,180,216,.25) 100%)' }} />
        <div className="absolute inset-0 opacity-20 grid-pattern" />
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 w-full fade-in">
            <div className="flex items-center gap-4 mb-6">
              <span className="h-[3px] w-16 bg-thunder" />
              <span className="text-thunder font-display tracking-[0.4em] text-xs">// VERIFIED REVIEWS</span>
            </div>
            <h1 className="font-display font-bold text-white text-4xl sm:text-6xl md:text-8xl leading-[0.88]">
              THE <span className="text-thunder">VERDICT</span>
            </h1>
            <p className="text-white/80 text-lg sm:text-xl max-w-xl mt-6 font-light">
              Real reviews from real clients. No fluff. No fakes. Just honest results.
            </p>
          </div>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="relative py-28 bg-charcoal-deep overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <ScrollReveal key={i} delay={i * 80}>
                <div
                  className={`relative p-8 h-full flex flex-col ${
                    t.placeholder
                      ? 'bg-charcoal-mid/20 border border-dashed border-white/20'
                      : 'bg-charcoal border-t-4 border-thunder hover:-translate-y-2 transition-transform duration-300'
                  }`}
                >
                  <div className="absolute -top-5 left-8 bg-charcoal-deep px-3 font-display text-electric text-xs tracking-[0.3em]">
                    REVIEW 0{i + 1}
                  </div>
                  <div className="text-thunder text-2xl mb-6">{'★'.repeat(t.rating)}</div>
                  <p className={`text-xl leading-snug mb-8 font-display font-medium flex-grow ${
                    t.placeholder ? 'text-white/30 italic' : 'text-white'
                  }`}>
                    &ldquo;{t.quote}&rdquo;
                  </p>
                  <div className="pt-5 border-t border-white/10">
                    <div className={`font-display font-bold tracking-wider ${t.placeholder ? 'text-white/30' : 'text-white'}`}>
                      {t.name}
                    </div>
                    <div className="text-white/50 text-xs tracking-[0.2em] uppercase mt-1">{t.city}</div>
                    <div className="mt-2">
                      <span className={`inline-block font-display tracking-wider text-[10px] px-3 py-1 border ${
                        t.placeholder ? 'border-white/10 text-white/20' : 'border-electric/40 text-electric'
                      }`}>
                        {t.service?.toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Leave a Review CTA */}
      <section className="relative py-28 bg-charcoal overflow-hidden">
        <ScrollReveal className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <div className="inline-flex items-center gap-3 mb-6">
            <span className="h-[2px] w-10 bg-thunder" />
            <span className="text-thunder font-display tracking-[0.4em] text-xs">// YOUR TURN</span>
            <span className="h-[2px] w-10 bg-thunder" />
          </div>
          <h2 className="font-display font-bold text-white text-5xl sm:text-6xl leading-[0.9] mb-6">
            HAD A GREAT <span className="text-thunder">EXPERIENCE?</span>
          </h2>
          <p className="text-white/60 text-lg mb-10 max-w-xl mx-auto">
            We'd love to hear about it. Leave us a review on Google and help other Central Florida residents find a crew they can trust.
          </p>
          {/* Google Review link verified April 15 2026 — temporarily disabled
          <a
            href="https://g.page/r/CT-n2SDV_OOKEBI/review"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-electric hover:bg-electric-bright text-white font-display font-bold px-12 py-5 text-sm tracking-[0.3em] transition glow-electric"
          >
            LEAVE A GOOGLE REVIEW →
          </a>
          */}
        </ScrollReveal>
      </section>

      {/* Book CTA */}
      <section className="relative py-28 bg-charcoal-deep overflow-hidden">
        <ScrollReveal className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-display font-bold text-white text-5xl sm:text-6xl leading-[0.9] mb-8">
            WANT TO BE OUR <span className="text-electric">NEXT 5-STAR?</span>
          </h2>
          <div className="flex flex-col sm:flex-row gap-5 justify-center">
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('openContactForm'))}
              className="pulse-btn bg-electric hover:bg-electric-bright text-charcoal-deep font-display font-bold px-12 py-5 text-sm tracking-[0.3em] transition glow-electric w-full sm:w-auto"
            >
              BOOK YOUR CLEAN →
            </button>
            <a
              href="tel:4074616039"
              className="border-2 border-thunder text-thunder hover:bg-thunder hover:text-charcoal-deep font-display font-bold px-12 py-5 text-sm tracking-[0.3em] transition w-full sm:w-auto text-center"
            >
              CALL (407) 461-6039
            </a>
          </div>
        </ScrollReveal>
      </section>
    </div>
  );
}
